import {useCallback, useEffect, useState} from 'react';

import useMediaQuery from '@material-ui/core/useMediaQuery';
import {useTheme} from '@material-ui/core/styles';

import {CompoundEntityRef} from '@backstage/catalog-model';
import {useAnalytics, useApi} from '@backstage/core-plugin-api';
// import { scmIntegrationsApiRef } from '@backstage/integration-react';
import {techdocsStorageApiRef, useShadowDomStylesLoading,} from '@backstage/plugin-techdocs-react';

import {
  addBaseUrl,
  addLinkClickListener,
  addSidebarToggle,
  copyToClipboard,
  onCssReady,
  removeMkdocsHeader,
  rewriteDocLinks,
  scrollIntoNavigation,
  simplifyMkdocsFooter,
  transform as transformer,
  useSanitizerTransformer,
  useStylesTransformer,
} from './transformers';
import {useTechDocsReader} from "./TechDocsReaderProvider";
import {useNavigateUrl} from "./useNavigateUrl";
import {catalogApiRef, useEntity} from "@backstage/plugin-catalog-react";
import {convertShortcodes} from "./transformers/convertShortcodes";
import {RepositoryEntityV1alpha1} from "../../../../../backend/src/custom-processors/types";

const MOBILE_MEDIA_QUERY = 'screen and (max-width: 76.1875em)';

/**
 * Hook that encapsulates the behavior of getting raw HTML and applying
 * transforms to it in order to make it function at a basic level in the
 * Backstage UI.
 */
export const useTechDocsReaderDom = (
  entityRef: CompoundEntityRef,
): Element | null => {
  const navigate = useNavigateUrl();
  const theme = useTheme();
  const {entity} = useEntity();
  const isMobileMedia = useMediaQuery(MOBILE_MEDIA_QUERY);
  const sanitizerTransformer = useSanitizerTransformer();
  const stylesTransformer = useStylesTransformer();
  const analytics = useAnalytics();

  const techdocsStorageApi = useApi(techdocsStorageApiRef);
  // const scmIntegrationsApi = useApi(scmIntegrationsApiRef);

  const {state, path, content: rawPage} = useTechDocsReader();

  const [dom, setDom] = useState<HTMLElement | null>(null);
  const isStyleLoading = useShadowDomStylesLoading(dom);

  const updateSidebarPositionAndHeight = useCallback(() => {
    if (!dom) return;

    const sidebars = dom.querySelectorAll<HTMLElement>('.md-sidebar');

    sidebars.forEach(element => {
      // set sidebar position to render in correct position
      if (isMobileMedia) {
        element.style.top = '0px';
      } else {
        const page = document?.querySelector('.techdocs-reader-page');
        const pageTop = page?.getBoundingClientRect().top ?? 0;
        let domTop = dom.getBoundingClientRect().top ?? 0;

        const tabs = dom.querySelector('.md-container > .md-tabs');
        const tabsHeight = tabs?.getBoundingClientRect().height ?? 0;

        // the sidebars should not scroll beyond the total height of the header and tabs
        if (domTop < pageTop) {
          domTop = pageTop;
        }

        const scrollbarTopPx = Math.max(domTop, 0) + tabsHeight;

        element.style.top = `${scrollbarTopPx}px`;

        // set scrollbar height to ensure all links can be seen when content is small
        const footer = dom.querySelector('.md-container > .md-footer');
        // if no footer, fallback to using the bottom of the window
        const scrollbarEndPx =
          footer?.getBoundingClientRect().top ?? window.innerHeight;

        element.style.height = `${scrollbarEndPx - scrollbarTopPx}px`;
      }

      // show the sidebar only after updating its position
      element.style.setProperty('opacity', '1');
    });
  }, [dom, isMobileMedia]);

  useEffect(() => {
    window.addEventListener('resize', updateSidebarPositionAndHeight);
    window.addEventListener('scroll', updateSidebarPositionAndHeight, true);
    return () => {
      window.removeEventListener('resize', updateSidebarPositionAndHeight);
      window.removeEventListener(
        'scroll',
        updateSidebarPositionAndHeight,
        true,
      );
    };
  }, [dom, updateSidebarPositionAndHeight]);

  // dynamically set width of footer to accommodate for pinning of the sidebar
  const updateFooterWidth = useCallback(() => {
    if (!dom) return;
    const footer = dom.querySelector<HTMLElement>('.md-footer');
    if (footer) {
      footer.style.width = `${dom.getBoundingClientRect().width}px`;
    }
  }, [dom]);

  useEffect(() => {
    window.addEventListener('resize', updateFooterWidth);
    return () => {
      window.removeEventListener('resize', updateFooterWidth);
    };
  }, [dom, updateFooterWidth]);

  // an update to "state" might lead to an updated UI so we include it as a trigger
  useEffect(() => {
    if (!isStyleLoading) {
      updateFooterWidth();
      updateSidebarPositionAndHeight();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state,
    isStyleLoading,
    updateFooterWidth,
    updateSidebarPositionAndHeight,
  ]);

  const catalogApiClient = useApi(catalogApiRef);

  // a function that performs transformations that are executed prior to adding it to the DOM
  const preRender = useCallback(
    (rawContent: string, contentPath: string) =>
      transformer(rawContent, [
        sanitizerTransformer,
        addBaseUrl({
          techdocsStorageApi,
          entityId: entityRef,
          path: contentPath,
        }),
        rewriteDocLinks(catalogApiClient, entity),
        convertShortcodes(entity as RepositoryEntityV1alpha1), // this needs to come AFTER rewriteDocLinks because it builds off of it
        addSidebarToggle(),
        removeMkdocsHeader(),
        simplifyMkdocsFooter(),
        // addGitFeedbackLink(scmIntegrationsApi),
        stylesTransformer,
      ]),
    [
      // only add dependencies that are in state or memorized variables to avoid unnecessary calls between re-renders
      entityRef,
      // scmIntegrationsApi,
      techdocsStorageApi,
      sanitizerTransformer,
      stylesTransformer,
    ],
  );

  // a function that performs transformations that are executed after adding it to the DOM
  const postRender = useCallback(
    async (transformedElement: Element) =>
      transformer(transformedElement, [
        scrollIntoNavigation(),
        copyToClipboard(theme),
        addLinkClickListener({
          baseUrl: window.location.origin,
          onClick: (event: MouseEvent, url: string) => {
            // detect if CTRL or META keys are pressed so that links can be opened in a new tab with `window.open`
            const modifierActive = event.ctrlKey || event.metaKey;
            const parsedUrl = new URL(url);

            // capture link clicks within documentation
            const linkText =
              (event.target as HTMLAnchorElement | undefined)?.innerText || url;
            const to = url.replace(window.location.origin, '');
            analytics.captureEvent('click', linkText, {attributes: {to}});

            // hash exists when anchor is clicked on secondary sidebar
            if (parsedUrl.hash) {
              if (modifierActive) {
                window.open(url, '_blank');
              } else {
                navigate(url);
                // Scroll to hash if it's on the current page
                transformedElement
                  ?.querySelector(`[id="${parsedUrl.hash.slice(1)}"]`)
                  ?.scrollIntoView();
              }
            } else {
              if (modifierActive) {
                window.open(url, '_blank');
              } else {
                navigate(url);
              }
            }
          },
        }),
        // disable MkDocs drawer toggling ('for' attribute => checkbox mechanism)
        onCssReady({
          onLoading: () => {
          },
          onLoaded: () => {
            transformedElement
              .querySelector('.md-nav__title')
              ?.removeAttribute('for');
          },
        }),
        // hide sidebars until their positions are updated
        onCssReady({
          onLoading: () => {
            const sidebars = Array.from(
              transformedElement.querySelectorAll<HTMLElement>('.md-sidebar'),
            );
            sidebars.forEach(element => {
              element.style.setProperty('opacity', '0');
            });
          },
          onLoaded: () => {
          },
        })
      ]),
    [theme, navigate, analytics],
  );

  useEffect(() => {
    if (!rawPage) return () => {
    };

    // if false, there is already a newer execution of this effect
    let shouldReplaceContent = true;

    // Pre-render
    preRender(rawPage, path).then(async preTransformedDomElement => {
      if (!preTransformedDomElement?.innerHTML) {
        return; // An unexpected error occurred
      }

      // don't manipulate the shadow dom if this isn't the latest effect execution
      if (!shouldReplaceContent) {
        return;
      }

      // Scroll to top after render
      window.scroll({top: 0});

      // Post-render
      const postTransformedDomElement = await postRender(
        preTransformedDomElement,
      );
      setDom(postTransformedDomElement as HTMLElement);
    });

    // cancel this execution
    return () => {
      shouldReplaceContent = false;
    };
  }, [rawPage, path, preRender, postRender]);

  return dom;
};
